// noinspection ES6UnusedImports
// noinspection ES6CheckImport

import Logger from '@/logger'
import Router from '@/router'

import ServiceFactory from '@/services/service-factory'

import Storage from '@/storage'
import Store from '@/store'

import ServiceOperation from '@/services/service-operation'

import PlanBuilderManager from '@/plan-builder-manager'
import RenovationsManager from '@/renovations-manager'
import SchemeServicingManager from '@/scheme-servicing-manager'

import { ACTIONS } from '@/store/actions'
import { MUTATIONS } from '@/store/mutations'

/* eslint-disable-next-line no-unused-vars */
import Project from '@/models/project'
/* eslint-disable-next-line no-unused-vars */
import Property from '@/models/property'
import Renovation from '@/models/renovation'

const loggingCategory = 'Auth'

/**
 * Checks to see if a householder is signed in.
 * @param {Boolean} reviewMigrations A flag. Pass `true` to evaluate migrations status.
 * @param {?string} bearerToken Optional bearer token to override any in state.
 * @returns {Promise<ServiceOperation>} The operation result.
 */
const checkHouseholderSignedInAsync = async function(reviewMigrations = false, bearerToken = null) {
    try {
        const testService = ServiceFactory.test()
        const operationState = await testService.isAuthorisedHouseholderAsync(bearerToken)

        if (operationState.error)
            operationState.throw()

        if (operationState.data.user) {

            await Storage.context.persistAsync(operationState.data.user, bearerToken)

            if (reviewMigrations)
                await Storage.versioning.updateSchemaAndResetAsync()
        } else {

            const cxt = Store.state.authContext

            if (cxt && cxt.type === 'account') {
                operationState.data.showLoginPrompt = true
                operationState.data.showLoginFor = cxt.id
                await Storage.clearContextAsync()
            } else {
                operationState.data.showLoginPrompt = false
                operationState.data.showLoginFor = null
            }
        }
        return operationState
    } catch (err) {
        Logger.warn(`Unable to check whether a householder is signed in: ${ err.message }`, loggingCategory)
        throw err
    }
}

/**
 * Loads the current project object in VUEX from renovation data stored in the database.
 * @return {Promise<{ improvementsLoaded: boolean, loadedRenovation: ?Renovation, projectFromRenovation: Project|null }>} Information following the reload.
 */
const loadCurrentProjectFromRenovationSourceAsync = async function() {
    let improvementsLoaded = false
    let loadedRenovation = null
    let projectFromRenovation = null

    if (!Store.state.currentProject || !Store.state.currentProject.initialised) {
        const planName = Store.state.currentProject && Store.state.currentProject.name
            ? Store.state.currentProject.name
            : Renovation.DEFAULT_NAME
        Logger.log(`Triggering project load from renovation source for plan ${ planName }`, loggingCategory)

        loadedRenovation = await RenovationsManager
            .loadAsync(planName, { allowNull: true })
        if (loadedRenovation) {
            Logger.logObject(loadedRenovation, `Plan (${ planName }) loaded for user`, loggingCategory)
            projectFromRenovation = await PlanBuilderManager.loadProjectFromRenovationAsync(loadedRenovation)
        } else {
            Logger.log('No plan currently available to load for user', loggingCategory)
        }
    }

    if (Store.state.property) {
        Logger.log('Triggering post-renovation load calculation check', loggingCategory)
        try {
            improvementsLoaded = await PlanBuilderManager.ApiData.loadInitiativesAsync(Store.state.property.id) !== null
        } catch (err) {
            throw err
        }
    }

    return { improvementsLoaded, loadedRenovation, projectFromRenovation }
}

/*
 * The AuthManager deals with authentication and authorisation concerns.
 * This responsibility may also involve User data manipulation but only when auth is involved (register / delete).
 * For other User manipulation tasks refer to the UserManager module.
 */
const AuthManager = {

    /**
     * Checks whether the current guest context is still valid.
     * @returns {Promise<boolean>} A flag to indicate the state of things.
     */
    async checkContextStillValidAsync() {
        const cxt = await Store.dispatch(ACTIONS.ensureAuthContext)

        if (cxt == null || cxt.id === 'TEMP-GUEST' || cxt.id.startsWith('guest-'))
            return false

        if (cxt.type === 'account')
            return true

        const authService = ServiceFactory.auth()
        const validGuest = await authService.loadGuestAsync(cxt.id)

        return validGuest.succeeded && validGuest.data != null
    },

    /**
     * Checks that recaptcha has been verified.
     * @returns {Promise<boolean>} A flag to indicate success.
     */
    async checkReCaptchaVerificationAsync() {
        try {
            const service = ServiceFactory.captcha()
            const operationState = await service.isVerifiedAsync()

            if (operationState.error) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error(operationState.error)
            }

            return operationState.data.result
        } catch (err) {
            Logger.warn(`Unable to determine ReCaptcha status: ${ err }`, 'ReCaptcha')
            return false
        }
    },

    /**
     * Checks the sign in status for a user.
     * @param {boolean} reviewMigrations A flag. Pass `true` to evaluate migrations status.
     * @param {?string} bearerToken Optional new bearer token.
     * @returns {Promise<{ signedIn: boolean, showLogin: ?boolean, user: ?string } | { signedIn: boolean }>}
     */
    async checkSignInStatusAsync(reviewMigrations = false, bearerToken = null) {
        try {

            const result = await checkHouseholderSignedInAsync(reviewMigrations, bearerToken)
            return {
                signedIn: result && result.data ? result.data.authorised : false,
                showLogin: result && result.data ? result.data.showLoginPrompt : false,
                user: result && result.data ? result.data.showLoginFor : null,
            }
        } catch (err) {
            Logger.warn(`Unable to determine Signed in status: ${ err }`)
            return {
                signedIn: false,
            }
        }
    },

    /**
     * Deletes the account for the currently logged-in user.
     * @returns {Promise<ServiceOperation>} The operation result.
     */
    async deleteAccountAsync() {
        Logger.log('Deleting the currently logged in user', 'Auth')

        const bearerToken = Store.state.authContext.rawToken

        // We have to clear state first, or we won't have permission.
        await Storage.clearContextAsync({ clearPreferences: true, clearData: true })
        await Storage.clearGuestContextAsync()

        const service = ServiceFactory.profile()
        const operationState = await service.deleteUserAsync(bearerToken)

        if (!operationState.succeeded)
            operationState.throw()

        return operationState
    },

    /**
     * Triggers the forgotten password process.
     * @param {string} email
     * @returns {Promise<ServiceOperation>} The operation state.
     */
    async forgottenPasswordAsync(email) {
        const service = ServiceFactory.auth()
        const operationState = await service.forgotPasswordAsync(email)

        if (!operationState.succeeded)
            operationState.throw()

        return operationState
    },

    /**
     * Logs an external user into the system.
     * @param {string} key The user's key.
     * @returns {Promise<number>|Promise} The address id
     */
    async logInExternalAsync(key) {

        if (!key)
            throw new TypeError('A key must be provided in order to log in.')

        Logger.log('Logging in', loggingCategory)
        const service = ServiceFactory.auth()
        const operationState = await service.logInExternalAsync(key)

        if (!operationState.succeeded) {
            operationState.throw()
        } else {

            const addressId = operationState.data ? operationState.data.defaultAddressId : undefined

            await Storage.clearGuestContextAsync()
            await Storage.context.persistExternalUserAsync(operationState.data.userName, operationState.data.token)
            await Storage.versioning.updateSchemaAndResetAsync()

            return addressId
        }
    },

    /**
     * Logs a user into the system.
     * @param {string} email The user's email, used to form the username.
     * @param {string} password The password.
     * @returns {Promise<ServiceOperation>} The operation state.
     */
    async logInAsync(email, password) {
        if (!email || !password)
            throw new TypeError('An email address and password must be provided in order to log in.')

        Logger.log('Logging in', loggingCategory)
        const service = ServiceFactory.auth()
        const operationState = await service.logInAsync(email, password)

        if (!operationState.succeeded) {
            operationState.throw()
        } else {

            await Storage.clearGuestContextAsync()
            await Storage.context.persistAsync(operationState.data.userName, operationState.data.token)
            await Storage.versioning.updateSchemaAndResetAsync()
        }

        return operationState
    },

    /**
     * Logs out the currently logged-in user.
     * @returns {Promise<ServiceOperation>}
     */
    async logOutAsync() {
        Logger.log('Logging out local...', loggingCategory)
        const operationState = new ServiceOperation('Logging Out', true)

        Store.commit(MUTATIONS.resetState)
        await Storage.clearContextAsync()
        operationState.complete(true, operationState.data)
        return operationState
    },

    /**
     * Logs out the currently active guest account.
     * @returns {Promise<ServiceOperation>}
     */
    async logOutGuestAsync() {
        return await this.logOutAsync()
    },

    /**
     * Registers a new user in the system.
     * @param {?string} email
     * @param {?string} password
     * @param {?string} confirmPassword
     * @param {?Property} property
     * @param {?string} firstName
     * @param {?string} lastName
     * @param {?string} contactTelephoneNumber
     * @param {?boolean} marketingConsentGiven
     * @param {?boolean} evaluationConsentGiven
     * @param {boolean} termsAccepted
     * @param {boolean} requireMarketingConsent
     * @param {boolean} requireEvaluationConsent
     * @returns {Promise<{ signedIn: boolean, improvementsLoaded: boolean, renovationLoaded: boolean, loadedRenovation: ?Renovation }>}
     */
    async registerAsync(email, password, confirmPassword, property,
        firstName, lastName, contactTelephoneNumber,
        marketingConsentGiven, evaluationConsentGiven, termsAccepted,
        requireMarketingConsent, requireEvaluationConsent) {
        if (!email)
            throw new Error('The email address is required.')

        if (password !== confirmPassword)
            throw new Error('The password and password confirmation must match.')

        const servicedScheme = await SchemeServicingManager.getDefaultServicedSchemeAsync()
        const schemeId = servicedScheme !== null ? servicedScheme.id : Store.state.scheme.id
        const partnerLinkId = Store.state.partnerReferralId

        Logger.log(`Storing partnerReferralId ${ partnerLinkId }`, loggingCategory)

        const service = ServiceFactory.auth()
        const operationState = await service.registerAsync({
            addressId: property.id,
            email,
            password,
            firstName,
            lastName,
            contactTelephoneNumber,
            marketingConsentGiven,
            evaluationConsentGiven,
            termsAccepted,
            requireMarketingConsent,
            requireEvaluationConsent,
            partnerLinkId,
            schemeId,
        })

        if (!operationState.succeeded) operationState.throw()

        const newContextId = operationState.data.userName
        const newToken = operationState.data.token
        Logger.logObject(operationState, 'Register operation state', loggingCategory)
        Logger.log(`Transferring guest context to new user context ${ newContextId }`, loggingCategory)
        await Storage.cloneDataAsync(newContextId)
        await Storage.clearContextAsync({
            clearData: false,
            keepProperty: true,
        })

        Logger.log(`Reinitialising sign in status with new account details`, loggingCategory)
        const signedInResponse = await AuthManager.checkSignInStatusAsync(false, newToken)
        const { improvementsLoaded, loadedRenovation } = await loadCurrentProjectFromRenovationSourceAsync()

        const response = {
            signedIn: signedInResponse.signedIn,
            improvementsLoaded,
            renovationLoaded: loadedRenovation !== null,
            loadedRenovation,
        }

        Logger.logObject(response, 'Registration operation response', loggingCategory)
        return response
    },

    /**
     * Initiates the router location according to the auth state of the application.
     * @returns {Promise}
     */
    async startAtAppropriateRouteForStateAsync() {
        if (Router.currentRoute.name === 'LogInWithContext' || Router.currentRoute.name === 'ExternalUserLogin')
            return

        if (!await AuthManager.checkContextStillValidAsync())
            await Storage.clearContextAsync({ clearData: true })

        Logger.log(`Rerouting app from ${ Router.currentRoute.name }`, 'Routing')
        const signInStatus = await AuthManager.checkSignInStatusAsync(true)
        const currentProperty = Store.state.property
        const currentProject = Store.state.improvements.currentProject
        const extUserId = Router.currentRoute.query['ext-user']

        Logger.logObject({ signInStatus, currentProperty, currentProject }, 'Redirection status dependencies', 'Routing')

        if (extUserId) {
            // noinspection JSCheckFunctionSignatures
            Router.push({
                name: 'ExternalUserLogin', params: {
                    key: extUserId,
                } })
        } else if (signInStatus.showLogin) {
            Router.push({ name: 'LogInWithContext', params: {
                key: signInStatus.user,
            } })
        } else if (signInStatus.signedIn) {
            Router.push({ name: 'Dashboard' })
        } else if (currentProperty) {
            if (currentProject && currentProject.initialised)
                Router.push({ name: 'PlanBuilder' })
            else
                Router.push({ name: 'MyHome', query: { id: currentProperty.id } })

        } else {
            Router.push({ name: 'Welcome' })
        }
    },

    /**
     * Creates a context for a guest and a matching back end owner entity when there's no user signed in and an address is selected.
     * @param {?Property} property An optional property.
     * @returns {Promise<ServiceOperation>}
     * @throws {Error} Unable to create context necessary to proceed as a guest user
     */
    async welcomeGuestIfNoLoggedInUserAsync(property = null) {
        Logger.logObject({ stateContext: Store.state.authContext }, 'Evaluating Auth Context', loggingCategory)
        if (Store.state.authContext == null || Store.state.authContext.id === 'TEMP-GUEST' || Store.state.authContext.id.startsWith('guest-')) {
            const authService = ServiceFactory.auth()
            const guestWelcome = await authService.createGuestAsync(property ? property.id : null)
            Logger.logObject({ createGuestServiceResult: guestWelcome, forProperty: property }, 'New Auth Context setting attempt with data', loggingCategory)

            if (!guestWelcome.succeeded) {
                const errMessage = `Unable to create context necessary to proceed as a guest user: ${ guestWelcome.error ? guestWelcome.error : guestWelcome.statusCode }`
                Logger.error(errMessage, loggingCategory)
                throw new Error(errMessage)
            } else {
                if (guestWelcome.data)
                    await Storage.context.persistGuestAsync(guestWelcome.data)
            }

            return guestWelcome
        }

        return ServiceOperation.immediate
    },
}

export default AuthManager
